import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import CompareChart from '../components/charts/compare/CompareChart'
import CompareNavigation from '../components/navigations/CompareNavigation'
import useDriverService from '@/modules/drivers/services/DriverService'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import useVehicleService from '@/modules/drivers/services/VehicleService'
import { DriverModel, VehicleModel } from '@/app/models'
import { formatDriverName } from '@/modules/drivers/utils/driverName'
import { ActivityInfo } from '@/modules/drivers/types/activities'

const Compare = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { getDrivers } = useDriverService()
  const { getVehicleList } = useVehicleService()
  const { setType, refreshTrigger } = useActivity()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [drivers, setDrivers] = useState<DriverModel[]>([])
  const [vehicles, setVehicles] = useState<VehicleModel[]>([])

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const [driversData, vehiclesData] = await Promise.all([
        getDrivers({ companyId, onlyActive: true }),
        getVehicleList({ companyId, onlyActive: true }),
      ])

      setDrivers(driversData || [])
      setVehicles(vehiclesData || [])
    } catch (error) {
      console.error('Error fetching data:', error)
      setDrivers([])
      setVehicles([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [companyId])

  React.useEffect(() => {
    setType('compare')
  }, [])

  React.useEffect(() => {
    if (refreshTrigger > 0) {
      fetchData()
    }
  }, [refreshTrigger])

  const [passedDays, setPassedDays] = useState<number>(0)
  const goToDate = (days: number) => {
    setPassedDays(days)
  }



  const [chart0Date, setChart0Date] = React.useState<string | null>(null)
  const [chart1DriverSuggestion, setChart1DriverSuggestion] = React.useState<
    number | null
  >(null)

  const handleChart0DataLoaded = React.useCallback(
    (data: ActivityInfo | null, _selectedDate: string | null) => {
      if (!data) {
        setChart1DriverSuggestion(null)
        return
      }
      const activities = (data.activities as any[]) || []
      const withCoDriver = activities.find(
        (a) =>
          typeof a?.coDriver === 'string' && a.coDriver.trim().length > 0,
      )
      if (!withCoDriver) {
        setChart1DriverSuggestion(null)
        return
      }
      const firstName = String(withCoDriver.coDriver).split(',')[0]?.trim()
      if (!firstName) return
      const matched = drivers.find((d) => {
        const full = d.fullName || formatDriverName(d)
        return full && full.toLowerCase() === firstName.toLowerCase()
      })
      if (!matched) {
        setChart1DriverSuggestion(null)
        return
      }
      setChart1DriverSuggestion(matched.id)
    },
    [drivers],
  )

  return (
    <div className="tw-flex tw-flex-1 tw-overflow-y-auto tw-flex-col tw-bg-white">
      <CompareNavigation goToDate={goToDate} />
      <div className="tw-flex tw-flex-1 tw-flex-col">
        <CompareChart
          setPassedDays={setPassedDays}
          passedDays={passedDays}
          companyId={companyId}
          isLoading={isLoading}
          drivers={drivers}
          vehicles={vehicles}
          onDataLoaded={handleChart0DataLoaded}
          onDateChange={setChart0Date}
        />
        <CompareChart
          setPassedDays={setPassedDays}
          passedDays={passedDays}
          companyId={companyId}
          isLoading={isLoading}
          drivers={drivers}
          vehicles={vehicles}
          suggestedDriverId={chart1DriverSuggestion}
          suggestedDate={chart0Date}
        />
        <CompareChart
          setPassedDays={setPassedDays}
          passedDays={passedDays}
          companyId={companyId}
          isLoading={isLoading}
          drivers={drivers}
          vehicles={vehicles}
          suggestedDate={chart0Date}
        />
        <CompareChart
          setPassedDays={setPassedDays}
          passedDays={passedDays}
          companyId={companyId}
          isLoading={isLoading}
          drivers={drivers}
          vehicles={vehicles}
          suggestedDate={chart0Date}
        />
      </div>
    </div>
  )
}

export default Compare
